import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Upload, CheckCircle, AlertCircle, Wrench, Search, ArrowRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const applianceTypes = [
  'Washer',
  'Dryer',
  'Refrigerator',
  'Freezer',
  'Oven/Range/Stove',
  'Dishwasher',
  'Garbage Disposal',
  'Microwave',
  'Other'
];

const PartsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    appliance_type: '',
    model_number: '',
    part_number: '',
    notes: ''
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('appliance_type', formData.appliance_type);
      formDataToSend.append('model_number', formData.model_number);
      formDataToSend.append('part_number', formData.part_number);
      formDataToSend.append('notes', formData.notes);
      if (image) {
        formDataToSend.append('image', image);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/parts-request`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: data.message || 'Thank you! We\'ll contact you shortly with part availability and pricing.' });
        setFormData({ name: '', email: '', phone: '', appliance_type: '', model_number: '', part_number: '', notes: '' });
        setImage(null);
        setImagePreview(null);
      } else {
        setStatus({ type: 'error', message: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Buy Appliance Parts Online | Ships Nationwide USA | BBM Appliances"
        description="Order appliance parts online - we ship nationwide to all 50 states! Washer parts, dryer parts, refrigerator parts, oven parts & more. OEM & aftermarket. Fast shipping. Request a part today!"
        canonical="https://www.bbmappliances.com/parts"
        keywords="buy appliance parts online, appliance parts nationwide, appliance parts USA, ship appliance parts, washer parts, dryer parts, refrigerator parts, oven parts, dishwasher parts, OEM parts, aftermarket parts, order appliance parts online"
      />
      <Navbar />
      <main data-testid="parts-page" className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-white to-orange-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 text-orange-600 rounded-full text-sm font-medium mb-6">
                <Wrench className="w-4 h-4" />
                Ships Nationwide
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                Buy Appliance Parts Online
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                We ship appliance parts to all 50 states! Tell us what you need and we'll find the right part. 
                We'll contact you with availability, pricing, and shipping options.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Ships Nationwide
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  OEM & Aftermarket Parts
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Competitive Pricing
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Search className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">Parts Request Form</h2>
                    <p className="text-sm text-muted-foreground">Fill out the details below</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="parts-request-form">
                  {/* Appliance Type */}
                  <div>
                    <label htmlFor="appliance_type" className="block text-sm font-medium text-foreground mb-2">
                      Appliance Type *
                    </label>
                    <select
                      id="appliance_type"
                      name="appliance_type"
                      value={formData.appliance_type}
                      onChange={handleChange}
                      required
                      data-testid="parts-appliance-select"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white"
                    >
                      <option value="">Select appliance type...</option>
                      {applianceTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Model & Part Numbers */}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="model_number" className="block text-sm font-medium text-foreground mb-2">
                        Model Number
                      </label>
                      <input
                        type="text"
                        id="model_number"
                        name="model_number"
                        value={formData.model_number}
                        onChange={handleChange}
                        data-testid="parts-model-input"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="e.g., WTW5000DW"
                      />
                    </div>

                    <div>
                      <label htmlFor="part_number" className="block text-sm font-medium text-foreground mb-2">
                        Part Number (if known)
                      </label>
                      <input
                        type="text"
                        id="part_number"
                        name="part_number"
                        value={formData.part_number}
                        onChange={handleChange}
                        data-testid="parts-partnumber-input"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                        placeholder="e.g., W10006355"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-foreground mb-2">
                      Describe the Part / Issue *
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      required
                      rows={4}
                      data-testid="parts-notes-input"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                      placeholder="Describe the part you need or the issue you're experiencing..."
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Upload Photo (optional)
                    </label>
                    {!imagePreview ? (
                      <label 
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      >
                        <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                        <span className="text-sm text-muted-foreground">Click to upload image</span>
                        <span className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB</span>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          data-testid="parts-image-input"
                        />
                      </label>
                    ) : (
                      <div className="relative inline-block">
                        <img 
                          src={imagePreview} 
                          alt="Part preview" 
                          className="max-h-40 rounded-xl border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="pt-4 border-t border-slate-100">
                    <h3 className="text-sm font-medium text-foreground mb-4">Your Contact Information</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="parts-name-input"
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                          placeholder="John Smith"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            data-testid="parts-email-input"
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            placeholder="john@email.com"
                          />
                        </div>

                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                            Phone *
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            data-testid="parts-phone-input"
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Message */}
                  {status.message && (
                    <div
                      data-testid="parts-status-message"
                      className={`flex items-center gap-2 p-4 rounded-xl ${
                        status.type === 'success' 
                          ? 'bg-green-50 text-green-700 border border-green-200' 
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {status.type === 'success' ? (
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      )}
                      <span>{status.message}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="parts-submit-btn"
                    className="w-full py-6 text-base font-medium"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Wrench className="w-5 h-5" />
                        Submit Parts Request
                      </span>
                    )}
                  </Button>
                </form>

                {/* Alternative Contact */}
                <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Prefer to talk? Call us directly:
                  </p>
                  <a 
                    href="tel:+18556056034"
                    className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    (855) 605-6034
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default PartsPage;
